<template>
  <LazyPdpPopupCampaignModal
    v-model="showPopUpModal"
    :show-loading="toCheckoutIsClicked"
    @on-click-go-to-checkout="goToCheckout()"
  />

  <LazyPricingPriceDetailsModal
    v-if="priceDetails"
    v-model="showPriceModal"
    :price-details="priceDetails"
  >
    <PdpLoanWarning class="my-32" />
  </LazyPricingPriceDetailsModal>

  <StickyFooterContainer>
    <div class="max-w-screen-xl w-full flex place-content-between px-0">
      <div class="flex flex-col md:flex-row md:gap-24">
        <StickyFooterPriceDisplay
          :strike-through-price-exists-and-differs="
            strikeThroughPriceExistsAndDiffers
          "
          :current-total-regular-monthly-price-to-display="
            productPageStore.currentTotalRegularMonthlyPriceToDisplay
          "
          :current-total-monthly-price-to-display="
            productPageStore.currentTotalMonthlyPriceToDisplay
          "
          :in-x-months-text="
            productPageStore.currentMonthlyPaymentTextToDisplay
          "
          :current-total-direct-price-to-display="
            productPageStore.currentTotalDirectPriceToDisplay
          "
          :current-full-price-string-for-screen-reader="
            productPageStore.currentFullPriceStringForScreenReader
          "
          :is-binding-period-active="
            productPageStore.selectedBindingPeriod !== 0
          "
          :is-monthly-payment="productPageStore.currentPaymentData?.Time !== 1"
          :show-only-direct-price="false"
          :action-button="{
            enabled: true,
            text: 'Se prisdetaljer',
            iconName: showPriceModal ? 'chevron-down' : 'chevron-up',
            ariaExpanded: showPriceModal,
          }"
          @button-click="showPriceModal = true"
        />
      </div>

      <AppButton
        :show-loading="toCheckoutIsClicked"
        @on-click="goToCheckout(true)"
      >
        Till kassan
      </AppButton>
    </div>
  </StickyFooterContainer>
</template>

<script lang="ts" setup>
import type { AggregatedBundlePriceDto } from '~/models/ListingProduct.api.model';
import {
  AddBundleRequestType,
  type AddBundleToCartRequestModel,
} from '~/models/shopping_cart/AddBundleToCartRequest.api.model';
import { usePdpPriceDetailsModal } from '~/composables/pricing/usePdpPriceDetailsModal';

const notifications = useNotificationStore();
const productPageStore = useMyProductPageStore();
const { priceDetails } = usePdpPriceDetailsModal();
const { pushAddToCartEvent } = useGoogleAnalyticsEvents();
const { $api } = useNuxtApp();

const showPriceModal = ref(false);
const showPopUpModal = ref(false);

watch(showPopUpModal, () => {
  productPageStore.productState.addedPopupDiscounts = [];
});

const toCheckoutIsClicked: Ref<boolean> = ref(false);

const strikeThroughPriceExistsAndDiffers = computed(
  () =>
    !!productPageStore.currentTotalRegularMonthlyPriceToDisplay &&
    productPageStore.currentTotalRegularMonthlyPriceToDisplay !==
      productPageStore.currentTotalMonthlyPriceToDisplay,
);

const AddDiscount = async () => {
  try {
    await $api.ShoppingCartModule.AddDiscountsToCart(
      productPageStore.productState.addedPopupDiscounts,
    );
  } catch (error) {
    notifications.setStandardErrorNotification();
  }
};

const goToCheckout = async (showPopupIsApplicable?: boolean) => {
  const selectedBundlePriceId = (
    productPageStore.currentBundlePrice as AggregatedBundlePriceDto | undefined
  )?.BundlePriceId;

  if (
    productPageStore.currentVariant?.DisableBuyButton ||
    !selectedBundlePriceId
  ) {
    notifications.setNewNotification({
      type: 'error',
      title: 'Slutsåld',
      msg: 'Denna produktvarianten är inte tillgänglig just nu.',
      isCloseable: true,
      link: {
        url: 'https://www.halebop.se/support',
        text: 'Kontakta supporten',
      },
    });

    return;
  }

  if (showPopupIsApplicable && !!productPageStore.popupDiscounts?.length) {
    showPopUpModal.value = true;
    return;
  }

  toCheckoutIsClicked.value = true;

  try {
    let requestType: AddBundleRequestType = AddBundleRequestType.SINGLE_PLAN;
    let familyBundlePriceId: number | undefined = undefined;

    if (
      productPageStore.isFamilyPlanSet &&
      productPageStore.productState.familyPlanDetails?.isNewCustomer === false
    ) {
      requestType = AddBundleRequestType.FAMILY_PLAN_EXISTING_CUSTOMER;
      familyBundlePriceId =
        productPageStore.productState?.familyPlanDetails
          ?.familyPlanBundlePriceId ?? undefined;
    } else if (productPageStore.isFamilyPlanSet) {
      requestType = AddBundleRequestType.FAMILY_PLAN_NEW_CUSTOMER;
      familyBundlePriceId =
        productPageStore.productState?.familyPlanDetails
          ?.familyPlanBundlePriceId ?? undefined;
    }

    const requestPayload: AddBundleToCartRequestModel = {
      BundlePriceId: selectedBundlePriceId,
      BundleRequestType: requestType,
      DownPayTime: +productPageStore.productState.selectedDownPaymentTime,
      FamilyBundlePriceId: familyBundlePriceId,
      MainFamilyPlanCellPhoneNumber:
        productPageStore.productState.familyPlanDetails
          ?.familyPlanParentPhoneNumber || undefined,
      NumberOfFamilyPlans:
        productPageStore.productState?.familyPlanDetails?.numberOfConnections ??
        undefined,
    };

    await $api.ShoppingCartModule.AddBundleToCart(requestPayload);

    if (productPageStore.productState.addedPopupDiscounts.length > 0) {
      await AddDiscount();
    }

    pushAddToCartEvent(productPageStore.trackingData);

    navigateTo('/shop/varukorg', { external: true });
  } catch (e) {
    console.error(e);
    notifications.setStandardErrorNotification();
    showPopUpModal.value = false;
    return;
  } finally {
    toCheckoutIsClicked.value = false;
  }
};
</script>

<style lang="scss" scoped>
.sticky {
  position: sticky;
  bottom: 0;
  z-index: 10;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 24px;
    background-color: white;
  }
}
</style>
